import * as endpoints from "../../lib/api/endpoints";
import * as error from "../../lib/api/error";
import * as queryStringHelpers from "../../lib/queryStringHelpers";
import * as routes from "../../constants/routes";
import ActionButton, {ActionButtonStyle} from "../ActionButton";
import CardBanner from "../CardBanner";
import ErrorBoundary from "../ErrorBoundary";
import Footer from "../Footer/Footer";
import logger from "../../lib/logger";
import React, {useEffect, useRef, useState} from "react";
import referImage from "../../images/refer.svg";
import referAFriendImage from "../../images/referAFriend.png";
import referAFriendImage2 from "../../images/referAFriend@2x.png";
import referAFriendImage3 from "../../images/referAFriend@3x.png";
import referAFriendImageDesktop from "../../images/referAFriendDesktop.png";
import referAFriendImageDesktop2 from "../../images/referAFriendDesktop@2x.png";
import referAFriendImageDesktop3 from "../../images/referAFriendDesktop@3x.png";
import repeatImage from "../../images/repeat.svg";
import ReviewCarousel from "../ReviewCarousel";
import rewardImage from "../../images/reward.svg";
import SetNoIndex from "../../lib/SetNoIndex";
import styles from "./ReferralPage.module.css";
import TextLink from "../TextLink";
import TitledContentBlocks, {ContentBlock} from "../TitledContentBlocks/TitledContentBlocks";
import usePageView from "../../lib/usePageView";
import {Column, Container, Row} from "../grid";
import {carouselReviews} from "../../lib/carouselReviews";
import {RouteComponentProps} from "react-router-dom";
import {StaticContext} from "react-router";

const strings = {
    agreement:
        "By clicking the “Refer My Friend” button, I acknowledge and confirm that I have read and agree to the Referral Program ",
    bannerDescription1: "Don’t miss out! Earn up to ",
    bannerDescription2:
        "annually for all your referrals. All students are eligible, and every time you refer a friend you can both earn a ",
    bannerDescription3: "statement credit.",
    bannerDescriptionAmount1: "$250 ",
    bannerDescriptionAmount2: "$50 ",
    bannerTitle: "For new cardholders, Refer a Friend!",
    errorMessage:
        "Unfortunately we don't have the information required to enroll you in the Referral Program at this time. Please contact Customer Service at 1-800-733-1732 to enroll.",
    howItWorks: "How it works",
    maybeLater: "Maybe Later",
    refer: "Refer",
    referAFriendImage: "three excited friends",
    referMyFriend: "Refer my Friend!",
    referDescription:
        "Keep your friends in the loop by sharing your referral link. Text, Twitter, Facebook, and email, share however you like!",
    repeat: "Repeat",
    repeatDescription: "Share your referral link now and earn up to $250!",
    reward: "Reward",
    rewardDescription:
        "When your friend applies, is approved, and activates the card, we’ll give each of you a $50 statement credit!",
    terms: "Terms and Conditions",
    title: "1FB USA Referral Landing",
};

const AUTO_SCROLL_TIME = 5000;

const TERMS_LINK = "/referral_program_terms_and_conditions.pdf";

function getReferAFriendImage() {
    return (
        <>
            <img
                className={styles.referImageDesktop}
                src={referAFriendImageDesktop}
                srcSet={`${referAFriendImageDesktop} 1x, ${referAFriendImageDesktop2} 2x, ${referAFriendImageDesktop3} 3x`}
                alt={strings.referAFriendImage}
            />
            <img
                className={styles.referImageMobile}
                src={referAFriendImage}
                srcSet={`${referAFriendImage} 1x, ${referAFriendImage2} 2x, ${referAFriendImage3} 3x`}
                alt={strings.referAFriendImage}
            />
        </>
    );
}

function getReferImage(imageStyle?: string) {
    return <img className={imageStyle} src={referImage} alt={strings.refer} />;
}

function getRewardImage(imageStyle?: string) {
    return <img className={imageStyle} src={rewardImage} alt={strings.refer} />;
}

function getRepeatImage(imageStyle?: string) {
    return <img className={imageStyle} src={repeatImage} alt={strings.refer} />;
}

const contentBlocks: ContentBlock[] = [
    {
        renderImage: getReferImage,
        title: strings.refer,
        content: strings.referDescription,
    },
    {
        renderImage: getRewardImage,
        title: strings.reward,
        content: strings.rewardDescription,
    },
    {
        renderImage: getRepeatImage,
        title: strings.repeat,
        content: strings.repeatDescription,
    },
];

// TODO: look into calls not logging in react dev tools

type RouterState = {
    isSecured?: boolean;
};

export default function ReferralPage(
    props: RouteComponentProps<undefined, StaticContext, RouterState>,
) {
    const isMountedRef = useRef<boolean>(false);
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        document.title = strings.title;
    }, []);

    SetNoIndex();
    usePageView(strings.title);

    const [loadingRefer, setLoadingRefer] = useState(false);
    const [loadingMaybe, setLoadingMaybe] = useState(false);
    const [disableRefer, setDisableRefer] = useState(false);
    const [disableMaybe, setDisableMaybe] = useState(false);
    const [clearInterval, setClearInterval] = useState(false);
    const [shortCode, setShortCode] = useState("");

    const queryParams = queryStringHelpers.objectify(window.location.search);

    function redirectUser(route: string) {
        if (!isMountedRef.current) {
            return;
        }
        // Note: Full round-trip since approved pages perform leadsRX calls
        setClearInterval(true);
        const destination = `${process.env.REACT_APP_BASE_PATH}${route}${window.location.search}`;
        window.location.replace(destination);
    }

    useEffect(() => {
        const qp = queryStringHelpers.objectify(window.location.search);
        if (!qp.dsn) {
            redirectUser(routes.REFERRAL_ERROR);
        }
    }, []);

    useEffect(() => {
        setDisableRefer(true);
        setDisableMaybe(true);
        endpoints
            .viewedReferAFriend()
            .then((data) => {
                if (!isMountedRef.current) {
                    return;
                }
                setShortCode(data.shortCode);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                logger.log(err);
                redirectUser(routes.REFERRAL_ERROR);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setDisableRefer(false);
                setDisableMaybe(false);
            });
    }, []);

    function onRefer() {
        endpoints
            .referAFriendDecision(false, shortCode)
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setClearInterval(true);
                props.history.push(`${routes.REFERRAL_SHARE}${window.location.search}`, {
                    isSecured: props.location.state?.isSecured ?? false,
                });
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                logger.log(err);
                redirectUser(routes.REFERRAL_ERROR);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoadingRefer(false);
                setDisableMaybe(false);
            });
    }

    function onMaybeLater() {
        endpoints
            .referAFriendDecision(true, shortCode)
            .then(() => {
                if (!isMountedRef.current) {
                    return;
                }
                props.location.state?.isSecured
                    ? redirectUser(routes.APPROVED_SECURED)
                    : redirectUser(routes.APPROVED_DEFAULT);
            })
            .catch((err) => {
                if (!isMountedRef.current) {
                    return;
                }
                logger.log(err);
                redirectUser(routes.REFERRAL_ERROR);
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                setLoadingMaybe(false);
                setDisableRefer(false);
            });
    }

    function callLeadBeforeRedirect(maybeLater: boolean) {
        let fieldName = "";
        if (maybeLater) {
            setLoadingMaybe(true);
            setDisableRefer(true);
            fieldName = "referral-maybe-later";
        } else {
            setLoadingRefer(true);
            setDisableMaybe(true);
            fieldName = "referral-accept";
        }
        endpoints
            .applicationLead(
                [
                    {
                        field: fieldName,
                        value: "",
                        timestamp: new Date().toISOString(),
                    },
                ],
                queryParams.dsn,
                window.location.href,
            )
            .catch((err) => {
                error.reportAxios(err, "callApplicationLead");
            })
            .finally(() => {
                if (!isMountedRef.current) {
                    return;
                }
                if (maybeLater) {
                    onMaybeLater();
                } else {
                    onRefer();
                }
            });
    }

    return (
        <ErrorBoundary>
            <CardBanner
                cardImage={getReferAFriendImage()}
                renderInterestRates={false}
                renderOnlyCard={true}
                logoClassName={styles.logo}
            >
                <div className={styles.bannerTitle}>{strings.bannerTitle}</div>
                <div className={styles.bannerDescription}>
                    {strings.bannerDescription1}
                    <span className={styles.bannerDescriptionBold}>
                        {strings.bannerDescriptionAmount1}
                    </span>
                    {strings.bannerDescription2}
                    <span className={styles.bannerDescriptionBold}>
                        {strings.bannerDescriptionAmount2}
                    </span>
                    {strings.bannerDescription3}
                </div>
                <div className={styles.bannerExtraInfoContainer}>
                    <div className={styles.bannerExtraInfo}>
                        {strings.agreement}
                        <TextLink
                            className={styles.bannerTextLink}
                            text={strings.terms}
                            to={TERMS_LINK}
                        />
                    </div>
                    <div className={styles.referButtonBannerContainer}>
                        <ActionButton
                            onClick={() => callLeadBeforeRedirect(false)}
                            className={styles.referButtonBanner}
                            label={strings.referMyFriend}
                            loading={loadingRefer}
                            disabled={disableRefer}
                            style={ActionButtonStyle.secondary}
                            fullWidth={true}
                        />
                    </div>
                </div>
            </CardBanner>
            <div className={styles.reviewCarouselContainer}>
                <ReviewCarousel
                    reviews={carouselReviews}
                    autoScrollTimeMilli={AUTO_SCROLL_TIME}
                    windowClassName={styles.carouselWindow}
                    clearInterval={clearInterval}
                    inviteCode={queryParams.dsn ?? undefined}
                />
            </div>
            <TitledContentBlocks
                header={strings.howItWorks}
                blocks={contentBlocks}
                containerPadding={styles.titledContentPadding}
                blockTitleFontClassName={styles.titleFont}
                blockContentFontClassName={styles.descriptionFont}
            />
            <Container className={styles.container}>
                <Row>
                    <Column largeOffset={2} large={8} mediumOffset={1} medium={6}>
                        <div className={styles.termsText}>
                            {strings.agreement}
                            <TextLink
                                className={styles.textLink}
                                text={strings.terms}
                                to={TERMS_LINK}
                            />
                        </div>
                    </Column>
                </Row>
                <Row>
                    <Column largeOffset={4} large={4} mediumOffset={2} medium={4}>
                        <div className={styles.buttonContainer}>
                            <ActionButton
                                onClick={() => callLeadBeforeRedirect(false)}
                                className={styles.referButton}
                                label={strings.referMyFriend}
                                loading={loadingRefer}
                                disabled={disableRefer}
                                style={ActionButtonStyle.secondary}
                            />
                            <ActionButton
                                onClick={() => callLeadBeforeRedirect(true)}
                                className={styles.maybeButton}
                                label={strings.maybeLater}
                                loading={loadingMaybe}
                                disabled={disableMaybe}
                                style={ActionButtonStyle.outlined}
                            />
                        </div>
                    </Column>
                </Row>
            </Container>
            <Footer />
        </ErrorBoundary>
    );
}
